import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { loginData } from "model/LoginModel";
import { useNavigate } from "react-router-dom";
import { fetchCandidateDetails } from "model/CandidateDetailsModel";

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState();
  const [candidateDetailsData, setCandidateDetailsData] = useState();
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingDetailsForUpdate, setLoadingDetailsForUpdate] = useState(false);
  const navigate = useNavigate();
  const detailsFetchedRef = useRef(false);

  const fetchCandidateDetailsData = useCallback(async () => {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return;

    setLoadingDetails(true);
    try {
      const response = await fetchCandidateDetails([]);
      if (response?.IsSuccess) {
        setCandidateDetailsData(response?.Data);
        detailsFetchedRef.current = true;
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const fetchCandidateDetailsDataForUpdate = useCallback(async () => {
    const authUser = localStorage.getItem("authUser");
    if (!authUser) return;

    setLoadingDetailsForUpdate(true);
    try {
      const response = await fetchCandidateDetails([]);
      if (response?.IsSuccess) {
        setCandidateDetailsData(response?.Data);
      } else {
        toast.error(response?.Message);
      }
    } catch (err) {
      toast.error(err);
    } finally {
      setLoadingDetailsForUpdate(false);
    }
  }, []);

  const loginUser = useCallback(
    async (formData) => {
      setLoading(true);
      try {
        const response = await loginData(formData);
        if (response?.IsSuccess) {
          const userDatas = response?.Data;
          setUserData(userDatas);
          const userDetails = { Name: userDatas?.Name, Username: userDatas?.Name };
          localStorage.setItem("authUser", JSON.stringify(userDatas?.AccessToken));
          localStorage.setItem("userData", JSON.stringify(userDetails));
          toast?.success("Login Successful");
          detailsFetchedRef.current = false;
          await fetchCandidateDetailsData();
          navigate(`/Home`);
        } else {
          toast?.error(response?.Message);
        }
      } catch (err) {
        toast.error(err.Message);
      } finally {
        setLoading(false);
      }
    },
    [fetchCandidateDetailsData, navigate]
  );

  // Fetch details once on mount only if already authenticated
  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser && !detailsFetchedRef.current) {
      fetchCandidateDetailsData();
    }
  }, [fetchCandidateDetailsData]);

  const value = useMemo(
    () => ({
      userData,
      loading,
      error,
      loginUser,
      candidateDetailsData,
      setCandidateDetailsData,
      fetchCandidateDetailsData,
      fetchCandidateDetailsDataForUpdate,
      loadingDetails,
      loadingDetailsForUpdate,
    }),
    [
      userData,
      loading,
      error,
      loginUser,
      candidateDetailsData,
      fetchCandidateDetailsData,
      fetchCandidateDetailsDataForUpdate,
      loadingDetails,
      loadingDetailsForUpdate,
    ]
  );

  return (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
